import * as THREE from 'three';

import type { AnimState } from './assets/roles.ts';
import type { PlayerState } from '../core/player.ts';
import { TICK_DT } from '../core/constants.ts';

/**
 * Drives a mage's clips from what the simulation already knows.
 *
 * No new state is invented here. `running`, `crouching`, `onGround`, `casting`,
 * `alive` and `ghost` are all decided in core and travel over the network for
 * free, so what a mage is *doing* is never something the renderer has to guess
 * at or the server has to send twice.
 *
 * Everything cross-fades. Snapping between clips is the single thing that most
 * makes a character read as a puppet rather than a person, and it costs one
 * argument.
 */

/** Seconds to blend between states. Long enough to smooth, short enough to read. */
const FADE = 0.16;
/** A faster blend where the change is an event rather than a mood. */
const FADE_SHARP = 0.07;

/**
 * How long the fall is given before the ghost gets up.
 *
 * A shade longer than the death clip itself, so the body has settled rather than
 * being snatched upright halfway down.
 */
const DEATH_SECONDS = 2.6;

/**
 * Speed above which a mage counts as moving, metres per second.
 *
 * Measured from the position rather than taken from input, because core does not
 * integrate the local player's movement — the renderer does — so input is the
 * one thing that is *not* true for everybody. Position is true for everybody.
 */
const MOVING = 0.35;
/** Above this, the jog reads as a sprint. */
const SPRINTING = 5.5;

/** Clips that play through once rather than looping. */
const ONE_SHOT: ReadonlySet<AnimState> = new Set([
  'castStart', 'castRelease', 'castEnd', 'hit', 'die',
]);

/** States whose playback rate follows the ground speed. */
const LOCOMOTION: ReadonlySet<AnimState> = new Set(['walk', 'jog', 'run', 'crouchWalk']);

/**
 * How far the playback rate is allowed from 1.
 *
 * Both ends exist for the same reason: past them the correction is worse than
 * the error it fixes. Below the floor the legs stop moving and the mage skates;
 * above the ceiling they blur, and a sprint at twice its authored rate reads as
 * a cartoon whatever the feet are doing.
 *
 * The ceiling binds in practice. `RUN_SPEED` is 7 m/s against a sprint clip
 * drawn for 3.29, which wants 2.13 and gets 1.8 — so about a sixth of the slide
 * survives. Honest options for the rest are a slower run or a faster clip, and
 * both are decisions rather than constants.
 */
const MIN_STRIDE_SCALE = 0.55;
const MAX_STRIDE_SCALE = 1.8;

/**
 * How far the body turns toward the way it is actually going, radians.
 *
 * Strafing with one forward clip is a mage sliding sideways while facing you,
 * which is the other half of what looked wrong. There are no strafe clips in
 * the pack, so the body leans into the movement instead: enough that the walk
 * is going roughly where the legs point, not so much that aim and body
 * disagree. Aim is untouched — this turns the model, never the crosshair.
 */
const MAX_LEAN = 0.34;
/** How quickly the lean follows a change of direction. Higher is snappier. */
const LEAN_RESPONSE = 7;

/**
 * How much of the ground speed a mage has to be putting sideways before the
 * body acknowledges it. Below this it is drift, and leaning into drift wobbles.
 */
const LEAN_DEADZONE = 0.25;

/**
 * Playback rate for a clip authored at one speed and played at another.
 *
 * Exported because it is the whole fix and it is arithmetic — worth a test that
 * does not need a skeleton, a mixer or a scene.
 */
export function strideScale(speed: number, authored: number | undefined): number {
  if (!authored || authored <= 0) return 1;
  const ratio = speed / authored;
  return Math.max(MIN_STRIDE_SCALE, Math.min(MAX_STRIDE_SCALE, ratio));
}

/**
 * Movement split into what is forward and what is sideways, as fractions of the
 * speed, in the mage's own frame.
 *
 * At yaw 0 forward is (0, 0, −1) and right is (+1, 0, 0), which is
 * `forwardVector`/`rightVector` in core — restated rather than imported because
 * the renderer may not reach into core for maths, and getting the sign wrong
 * here is how walking came out negated the last time two copies existed.
 */
export function movementBasis(
  vx: number, vz: number, yaw: number,
): { forward: number; lateral: number; speed: number } {
  const speed = Math.hypot(vx, vz);
  if (speed < 1e-4) return { forward: 0, lateral: 0, speed: 0 };
  const fx = -Math.sin(yaw);
  const fz = -Math.cos(yaw);
  return {
    forward: (vx * fx + vz * fz) / speed,
    lateral: (vx * fz - vz * fx) / speed,
    speed,
  };
}

/**
 * The lean a given sideways fraction asks for.
 *
 * Zero inside the deadzone and ramping to `MAX_LEAN` at a full sideways strafe,
 * so a diagonal leans a little and a pure strafe leans all it is going to.
 */
export function leanTarget(lateral: number): number {
  const magnitude = Math.abs(lateral);
  if (magnitude < LEAN_DEADZONE) return 0;
  const t = (magnitude - LEAN_DEADZONE) / (1 - LEAN_DEADZONE);
  return -Math.sign(lateral) * MAX_LEAN * Math.min(1, t);
}

export class MageAnimator {
  private readonly mixer: THREE.AnimationMixer;
  private readonly actions = new Map<AnimState, THREE.AnimationAction>();
  private current: AnimState | null = null;

  /** Set while a one-shot is playing and nothing should interrupt it. */
  private lockedFor = 0;

  private readonly lastPos = new THREE.Vector3();
  /**
   * Whether `lastPos` has ever been written.
   *
   * Without it the first frame measures the distance from the world origin to
   * wherever this mage spawned, divides by a frame, and reports a mage moving at
   * several thousand metres a second in a direction nobody chose. The speed
   * smoothing hid that while speed was all this class knew; a *direction* taken
   * from the same delta would have picked a lean and a backpedal out of it.
   */
  private primed = false;
  /** Smoothed world velocity. Direction as well as magnitude, now both are used. */
  private readonly velocity = new THREE.Vector3();
  private speed = 0;
  /** Current body turn toward the direction of travel, radians. */
  private lean = 0;
  /** Whether the locomotion clip is running backwards. */
  private reversed = false;
  private wasCasting = false;
  private wasAlive = true;
  /** Seconds since this mage went down, so the fall can finish before it rises. */
  private deadFor = 0;

  /** Metres per second each clip was drawn for. Empty means play it straight. */
  private readonly clipSpeeds: Partial<Record<AnimState, number>>;

  constructor(
    root: THREE.Object3D,
    clips: Partial<Record<AnimState, THREE.AnimationClip>>,
    clipSpeeds: Partial<Record<AnimState, number>> = {},
  ) {
    this.mixer = new THREE.AnimationMixer(root);
    this.clipSpeeds = clipSpeeds;

    for (const [state, clip] of Object.entries(clips)) {
      if (!clip) continue;
      const action = this.mixer.clipAction(clip);
      if (ONE_SHOT.has(state as AnimState)) {
        action.setLoop(THREE.LoopOnce, 1);
        // Death and a cast release both want to hold their last frame rather
        // than snapping back to a rest pose the moment they finish.
        action.clampWhenFinished = true;
      }
      this.actions.set(state as AnimState, action);
    }

    /*
     * Every clip is bound now rather than the first time it is needed.
     *
     * Three.js resolves an action's tracks to actual object properties on its
     * first `play()`, which for a skinned mage means building a `PropertyBinding`
     * per track — sixty-odd bones' worth of string parsing and object walking.
     * Paid lazily that lands on whichever frame a mage first dies, or first
     * casts, or first takes a hit, and reads as a stutter at exactly the moment
     * something dramatic happened.
     *
     * Playing and immediately stopping each one does the binding and leaves no
     * trace: weights are back to zero and the mixer has nothing running. This
     * runs while the loading screen is still up, which is where the cost belongs.
     */
    for (const action of this.actions.values()) {
      action.play();
      action.stop();
    }
    this.mixer.update(0);
  }

  update(p: PlayerState, dt: number): void {
    this.track(p, dt);

    this.lockedFor = Math.max(0, this.lockedFor - dt);
    if (!p.alive || p.ghost) this.deadFor += dt;
    const wanted = this.choose(p);
    if (wanted) this.play(wanted, ONE_SHOT.has(wanted) ? FADE_SHARP : FADE);

    this.drive(p, dt);
    this.mixer.update(dt);
  }

  /**
   * Where this mage is going, smoothed.
   *
   * Measured from the position rather than taken from input, because core does
   * not integrate the local player's movement — the renderer does — so input is
   * the one thing that is *not* true for everybody. Position is true for
   * everybody, which is what makes this work unchanged once the server exists.
   *
   * Smoothed, or a single stuttering frame flips a mage between walk and idle,
   * and now also between forward and backward.
   */
  private track(p: PlayerState, dt: number): void {
    const here = new THREE.Vector3(p.pos.x, p.pos.y, p.pos.z);
    if (!this.primed) {
      this.primed = true;
      this.lastPos.copy(here);
      return;
    }

    /*
     * Measured across the *tick*, not across the frame.
     *
     * `p.pos` is a simulation value and only changes sixty times a second. This
     * used to divide the change in it by the frame's `dt`, which is only the
     * same thing when the two rates match — and on a 240Hz display they do not.
     * Three frames in four saw no movement at all and reported a standstill; the
     * fourth saw a whole tick's worth over a quarter of a tick and reported four
     * times the real speed. Smoothed together that is a velocity whose direction
     * wanders, and direction is what decides the body's lean and whether the
     * walk cycle plays forwards or backwards — so a remote mage jittered between
     * leaning left and right and between walking and backpedalling, several
     * times a second, which reads as spinning.
     *
     * It only showed on remote mages because the local one is predicted every
     * frame and genuinely does move continuously, and it only showed on the fast
     * display because at 90Hz the aliasing is mild enough to smooth away.
     *
     * `prevPos` is where the simulation had this mage a tick ago, so the
     * difference over `TICK_DT` is the velocity the simulation actually gave
     * them — the same number at any frame rate.
     */
    const prev = p.prevPos ?? p.pos;
    const sim = {
      x: (p.pos.x - prev.x) / TICK_DT,
      y: (p.pos.y - prev.y) / TICK_DT,
      z: (p.pos.z - prev.z) / TICK_DT,
    };

    const step = Math.min(1, dt * 8);
    const vx = sim.x;
    const vy = sim.y;
    const vz = sim.z;
    // Clamped before smoothing: a teleport out of Valhalla is a single frame of
    // several hundred metres a second, and letting that into the average leaves
    // a mage sprinting on the spot for the second after they arrive.
    const raw = new THREE.Vector3(vx, vy, vz);
    if (raw.length() > 20) raw.setLength(20);
    this.velocity.lerp(raw, step);

    this.speed = Math.hypot(this.velocity.x, this.velocity.z);
    this.lastPos.copy(here);
  }

  /**
   * Playback rate and body lean, from the movement already measured.
   *
   * Runs every frame rather than only on a state change, because both follow a
   * speed and a direction that change continuously while the clip does not.
   */
  private drive(p: PlayerState, dt: number): void {
    const alive = p.alive && !p.ghost;
    const { forward, lateral } = movementBasis(this.velocity.x, this.velocity.z, p.yaw);
    const moving = alive && this.speed > MOVING && p.onGround;

    // Lean eases back to nothing when there is nothing to lean into, so a mage
    // who stops mid-strafe straightens up rather than staying cocked.
    const target = moving ? leanTarget(lateral) : 0;
    this.lean += (target - this.lean) * Math.min(1, dt * LEAN_RESPONSE);

    const state = this.current;
    if (!state || !LOCOMOTION.has(state)) {
      this.reversed = false;
      return;
    }
    const action = this.actions.get(state);
    if (!action) return;

    /*
     * Backing up plays the same clip in reverse.
     *
     * The pack has no backpedal, and the alternative — a forward run played
     * while the mage travels backwards — is the single most obviously wrong
     * thing a character can do. Reversed, the legs go the way the body does. It
     * is not a real backpedal animation and a trained eye will find it, but it
     * is right about the thing that matters and costs one sign.
     */
    this.reversed = moving && forward < -0.35;
    /*
     * A clip entered at time 0 with a negative rate underflows immediately, and
     * whether that clamps or wraps depends on the loop mode — either way the
     * first frame of a backpedal is a jump. Seeding to the far end starts it
     * where a reversed clip should start. Checked every frame rather than only
     * on the transition, because cross-fading from jog to run while already
     * backing up hands over a freshly reset action sitting at zero.
     */
    if (this.reversed && action.time <= 0) action.time = action.getClip().duration;

    const scale = strideScale(this.speed, this.clipSpeeds[state]);
    action.timeScale = this.reversed ? -scale : scale;
  }

  /**
   * How far the body is turned from where it is aiming, radians.
   *
   * Applied by the caller on top of `yaw` rather than here, because the mesh's
   * rotation is the caller's to own — the animator poses a skeleton and does not
   * place the thing it is inside.
   */
  get leanYaw(): number {
    return this.lean;
  }

  /**
   * What this mage should be doing.
   *
   * Order is priority. Death outranks everything, then the moment a cast is
   * released, then the wind-up, then whether they are off the ground, then what
   * their legs are doing.
   */
  private choose(p: PlayerState): AnimState | null {
    const alive = p.alive && !p.ghost;

    if (!alive) {
      if (this.wasAlive) {
        this.wasAlive = false;
        this.wasCasting = false;
        this.deadFor = 0;
        return 'die';
      }

      /*
       * A ghost is not a corpse.
       *
       * The death clip used to be played and then held for as long as the mage
       * was down — but a ghost walks: it drifts toward a Cleric hoping to be
       * called back. So the collapsed body slid around the floor, which is what
       * it looked like. Once the fall has finished the ghost stands and hovers,
       * and the idle is the closest thing the pack has to a float.
       */
      return this.deadFor > DEATH_SECONDS ? 'idle' : null;
    }
    this.wasAlive = true;

    // The instant a wind-up ends, whatever the reason. Letting go and being
    // interrupted look the same from outside, and the release sells both.
    const casting = p.casting !== null;
    if (this.wasCasting && !casting) {
      this.wasCasting = false;
      this.lockedFor = 0.35;
      return 'castRelease';
    }
    this.wasCasting = casting;

    if (casting) return this.actions.has('castHold') && this.current === 'castStart'
      ? 'castHold'
      : 'castStart';

    // A one-shot in progress owns the body until it is done.
    if (this.lockedFor > 0) return null;

    if (!p.onGround) return 'jump';
    if (p.crouching) return this.speed > MOVING ? 'crouchWalk' : 'crouchIdle';
    if (this.speed > SPRINTING) return 'run';
    if (this.speed > MOVING) return p.running ? 'run' : 'jog';
    return 'idle';
  }

  /** Cross-fades to a state, falling back through what the pack actually has. */
  private play(state: AnimState, fade: number): void {
    const action = this.actions.get(state) ?? this.fallbackFor(state);
    if (!action) return;
    if (this.current === state) return;

    const previous = this.current ? this.actions.get(this.current) : null;
    this.current = state;

    action.reset();
    action.enabled = true;
    action.setEffectiveWeight(1);
    action.play();
    if (previous && previous !== action) previous.crossFadeTo(action, fade, false);
  }

  /**
   * A pack without every clip should degrade rather than freeze.
   *
   * A missing crouch-walk is better served by a walk than by nothing, and a
   * missing anything is better served by the idle than by a mage locked in
   * whatever they were doing when the clip ran out.
   */
  private fallbackFor(state: AnimState): THREE.AnimationAction | undefined {
    const chain: Partial<Record<AnimState, AnimState[]>> = {
      jog: ['walk', 'run', 'idle'],
      run: ['jog', 'walk', 'idle'],
      walk: ['jog', 'run', 'idle'],
      crouchWalk: ['crouchIdle', 'walk', 'idle'],
      crouchIdle: ['idle'],
      jump: ['idle'],
      castHold: ['castStart', 'idle'],
      castStart: ['idle'],
      castRelease: ['castStart', 'idle'],
      castEnd: ['idle'],
      hit: ['idle'],
      die: ['idle'],
    };
    for (const next of chain[state] ?? []) {
      const action = this.actions.get(next);
      if (action) return action;
    }
    return undefined;
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.mixer.getRoot() as THREE.Object3D);
  }
}
